/**
 * BulkActions — reusable Import / Add / Export / Ask-assistant pill row.
 *
 * Drops onto any list-based page (Equipment, SOPs, Prep guides, Consent
 * templates, Screening forms, TLD readings, Lead aprons, Incidents, MBS,
 * DIAS attestations) and provides:
 *   • Import (xlsx / csv / docx / pdf) → preview-before-commit modal
 *   • Add (calls page-supplied onAdd handler — optional)
 *   • Export dropdown (xlsx / csv / pdf / docx / json)
 *   • Ask assistant (focuses + opens the global ChatBubble)
 *
 * Read-only resources show only Export + Ask assistant.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Upload, Plus, Download, ChevronDown, MessageSquare, FileText, FileSpreadsheet,
  FileCode2, FileType, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BulkPreviewModal from './BulkPreviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPORT_FORMATS = [
  { id: 'xlsx', label: 'Excel (.xlsx)',   Icon: FileSpreadsheet },
  { id: 'csv',  label: 'CSV',             Icon: FileText },
  { id: 'pdf',  label: 'PDF',             Icon: FileType },
  { id: 'docx', label: 'Word (.docx)',    Icon: FileText },
  { id: 'json', label: 'JSON',            Icon: FileCode2 },
];

export default function BulkActions({
  resource,
  title,
  addLabel = '',
  onAdd = null,
  readOnly = false,
  color = '#42CDD7',
  onAfterImport = null,
}) {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [exportOpen, setExportOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState('');
  const fileInputRef = useRef(null);
  const exportRef = useRef(null);

  // close export dropdown on outside click
  useEffect(() => {
    const onDoc = e => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const triggerImport = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/bulk/${resource}/preview`, fd, { withCredentials: true });
      setPreviewData({ ...r.data, filename: file.name });
    } catch (err) {
      alert(err.response?.data?.detail || 'Import preview failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  const exportAs = async (format) => {
    setExportOpen(false);
    setDownloading(format);
    try {
      const r = await axios.get(`${API}/bulk/${resource}/export?format=${format}`,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `${resource}_${today}.${format}`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || `Export ${format} failed`);
    }
    setDownloading('');
  };

  const downloadTemplate = async (format = 'xlsx') => {
    setExportOpen(false);
    try {
      const r = await axios.get(`${API}/bulk/${resource}/template?format=${format}`,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${resource}_template.${format}`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Template download failed');
    }
  };

  const askAssistant = () => {
    // Open the global ChatBubble. Listens via custom event 'ask-assistant'.
    window.dispatchEvent(new CustomEvent('ask-assistant', { detail: { context: title } }));
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`bulk-actions-${resource}`}>
      {!readOnly && isPM && (
        <>
          <input ref={fileInputRef} type="file" className="hidden"
            accept=".xlsx,.csv,.pdf,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleImportFile} data-testid={`bulk-import-input-${resource}`} />
          <button onClick={triggerImport} disabled={importing}
            data-testid={`bulk-import-${resource}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border bg-background hover:bg-muted/40 disabled:opacity-50"
            style={{ borderColor: `${color}50`, color: color }}>
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {importing ? 'Parsing…' : 'Import'}
          </button>
        </>
      )}

      {onAdd && isPM && !readOnly && (
        <button onClick={onAdd} data-testid={`bulk-add-${resource}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white"
          style={{ backgroundColor: color }}>
          <Plus className="w-3.5 h-3.5" /> {addLabel || 'Add'}
        </button>
      )}

      <div className="relative" ref={exportRef}>
        <button onClick={() => setExportOpen(o => !o)} data-testid={`bulk-export-${resource}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border bg-background hover:bg-muted/40">
          {downloading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color }} />
            : <Download className="w-3.5 h-3.5" style={{ color }} />}
          <span style={{ color }}>{downloading ? `Exporting ${downloading}…` : 'Export'}</span>
          <ChevronDown className="w-3 h-3" style={{ color }} />
        </button>
        {exportOpen && (
          <div className="absolute right-0 top-full mt-1 z-30 bg-background border border-border rounded-lg shadow-lg py-1 min-w-[180px]"
            data-testid={`bulk-export-menu-${resource}`}>
            {EXPORT_FORMATS.map(f => (
              <button key={f.id} onClick={() => exportAs(f.id)}
                data-testid={`bulk-export-${resource}-${f.id}`}
                className="w-full flex items-center gap-2 text-sm px-3 py-1.5 hover:bg-muted/50 text-left">
                <f.Icon className="w-3.5 h-3.5" style={{ color }} />
                <span>{f.label}</span>
              </button>
            ))}
            {!readOnly && isPM && (
              <>
                <div className="border-t border-border my-1" />
                <button onClick={() => downloadTemplate('xlsx')}
                  data-testid={`bulk-template-${resource}`}
                  className="w-full flex items-center gap-2 text-xs px-3 py-1.5 hover:bg-muted/50 text-left text-muted-foreground">
                  <FileSpreadsheet className="w-3 h-3" />
                  Blank import template (.xlsx)
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <button onClick={askAssistant} data-testid={`bulk-assistant-${resource}`}
        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white"
        style={{ backgroundColor: '#0E2D5C' }}>
        <MessageSquare className="w-3.5 h-3.5" /> Ask assistant
      </button>

      {previewData && (
        <BulkPreviewModal
          resource={resource}
          data={previewData}
          color={color}
          onClose={() => setPreviewData(null)}
          onCommitted={() => {
            setPreviewData(null);
            onAfterImport?.();
          }}
        />
      )}
    </div>
  );
}
